/**
 * Runtime configuration.
 *
 * The two backend URLs are baked in at build time by esbuild's `--define` (see
 * `client/build.ts` + `src/build-globals.d.ts`), so the web bundle carries its
 * own server/asset location. The Android wrapper doesn't need to inject any
 * config — it just bundles the variant that matches its build type.
 *
 * Two asset locations matter (see asset.ts):
 *   • appOrigin       – where the app itself was loaded from. Assets bundled
 *                       *with the client* live here (same-origin), so they load
 *                       offline and without hitting the server.
 *   • serverAssetBase – the configured backend. Used as a fallback for assets
 *                       the server has but the client bundle does not.
 *
 * The triple-tap "dev version" toggle in the title bar uses `appOrigin` to
 * detect whether we're running bundled or server-served (the two origins
 * differ in bundled mode, match in server-served mode) and tells the bridge
 * to switch.
 */

export interface WildHuntConfig {
  /** WebSocket URL of the Lowlander game server. */
  serverUrl: string;
  /** Base URL of the backend for server-side asset fallback (no trailing slash). */
  serverAssetBase: string;
}

import { harnessServerUrl } from './native/harnessEnv';

export const config: WildHuntConfig = {
  // Under the dev play-test harness, talk to the origin that served us so the
  // whole setup is self-contained on one local server; otherwise use the
  // build-time backend.
  serverUrl: harnessServerUrl() ?? __WILDHUNT_SERVER_URL__,
  serverAssetBase: __WILDHUNT_SERVER_ASSET_BASE__,
};

/**
 * Base URL the app bundle itself was served from (where bundled assets live).
 *
 * This must be the *directory* the document was loaded from, not just the
 * origin: the Android wrapper bundles the web app under `…/web/` (served by a
 * WebViewAssetLoader), so an asset like `voice/scan.mp3` lives at
 * `…/web/voice/scan.mp3`, not `…/voice/scan.mp3`. Stripping the filename from
 * `location.pathname` gives `/web` there and `''` when served from a server
 * root, so `join(appOrigin, path)` resolves correctly in both cases.
 */
export const appOrigin: string =
  typeof location !== 'undefined' && location.origin && location.origin !== 'null'
    ? location.origin + location.pathname.replace(/\/[^/]*$/, '')
    : config.serverAssetBase;
